import type { LineItem, Currency, VatTreatment } from "@/types";
import { computeTotals, formatMoney } from "@/lib/money";
import { resolveTokens } from "@/lib/lineItems";

/**
 * Read-only breakdown of an accepted quote: every mandatory line item plus any
 * optional item the client opted into (marked "Included"). Reuses the pricing
 * table styling (blocks.css, loaded globally) and the shared money helpers, so
 * currency/VAT handling matches the live pricing block exactly.
 *
 * `acceptedTotal` is the server's authoritative agreed figure — it, not the
 * recomputed total, is shown as the headline.
 */
export function AcceptedBreakdown({
  lineItems,
  selectedOptionalIds = [],
  currency = "GBP",
  vatTreatment = "inclusive",
  acceptedTotal,
  shortDescription,
}: {
  lineItems: LineItem[];
  selectedOptionalIds?: number[];
  currency?: Currency;
  vatTreatment?: VatTreatment;
  acceptedTotal?: number;
  shortDescription?: string;
}) {
  const selected = new Set(selectedOptionalIds);
  const totals = computeTotals(lineItems, vatTreatment, selectedOptionalIds);
  // Only the agreed rows: mandatory + the optional items that were ticked.
  const includedOptional = totals.optional.filter((li) => selected.has(li.id));
  const hasIncludedOptional = includedOptional.length > 0;

  if (totals.required.length === 0 && !hasIncludedOptional) return null;

  // Prefer the authoritative figure; fall back to the recomputed total.
  const headline = typeof acceptedTotal === "number" ? acceptedTotal : totals.total;

  return (
    <div className="accepted-breakdown">
      <p className="eyebrow">Agreed fees</p>
      <div className="pricing-table" role="table" aria-label="Agreed fees">
        <div className="pricing-table__head pricing-table__head--2col" role="row">
          <span role="columnheader">Item</span>
          <span role="columnheader" className="num">Amount</span>
        </div>

        {totals.required.map((li) => (
          <BreakdownRow key={li.id} item={li} currency={currency} shortDescription={shortDescription} />
        ))}

        {hasIncludedOptional ? (
          <>
            <div className="pricing-table__subhead" role="row">
              <span role="cell">Optional extras selected</span>
            </div>
            {includedOptional.map((li) => (
              <BreakdownRow key={li.id} item={li} currency={currency} included shortDescription={shortDescription} />
            ))}
          </>
        ) : null}
      </div>

      <dl className="pricing-summary">
        {hasIncludedOptional ? (
          <>
            <div className="pricing-summary__row">
              <dt>Base fees</dt>
              <dd className="num numeric">{formatMoney(totals.subtotal, currency)}</dd>
            </div>
            <div className="pricing-summary__row pricing-summary__row--addon">
              <dt>Optional extras selected</dt>
              <dd className="num numeric">+ {formatMoney(totals.selectedOptionalTotal, currency)}</dd>
            </div>
          </>
        ) : (
          <div className="pricing-summary__row">
            <dt>Subtotal</dt>
            <dd className="num numeric">{formatMoney(totals.subtotal, currency)}</dd>
          </div>
        )}
        <div className="pricing-summary__row">
          <dt>{totals.vatLabel}</dt>
          <dd className="num numeric">{formatMoney(totals.vat, currency)}</dd>
        </div>
        <div className="pricing-summary__row pricing-summary__row--total">
          <dt>Agreed total</dt>
          <dd className="num numeric">{formatMoney(headline, currency)}</dd>
        </div>
      </dl>
    </div>
  );
}

function BreakdownRow({
  item,
  currency,
  included = false,
  shortDescription,
}: {
  item: LineItem;
  currency: Currency;
  included?: boolean;
  shortDescription?: string;
}) {
  const title = resolveTokens(item.title, shortDescription);
  const description = resolveTokens(item.description, shortDescription);
  return (
    <div
      className={`pricing-table__row pricing-table__row--2col${included ? " pricing-table__row--optional pricing-table__row--selected" : ""}`}
      role="row"
    >
      <span role="cell" className="pricing-table__item" data-label="Item">
        <span className="pricing-table__title">
          {title}
          {included ? (
            <span className="pricing-table__badge pricing-table__badge--included">Selected</span>
          ) : null}
        </span>
        {description ? <span className="pricing-table__desc">{description}</span> : null}
      </span>
      <span role="cell" className="num numeric" data-label="Amount">
        {formatMoney(item.line_total, currency)}
      </span>
    </div>
  );
}
